import axios from 'axios';
import { errorHandler, logWarning } from './error-handler';

// Create axios instance
const apiClient = axios.create();

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add request timing
    config.metadata = { startTime: new Date() };
    return config;
  },
  (error) => {
    errorHandler(error, {
      tags: { errorType: 'request_interceptor' },
      extra: { config: error.config },
    });
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log slow requests
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;

    if (duration > 5000) {
      // Log requests taking more than 5 seconds
      logWarning(`Slow API request detected`, {
        url: response.config.url,
        duration: `${duration}ms`,
        status: response.status,
      });
    }

    return response;
  },
  (error) => {
    const { response, request, config } = error;

    // Network error
    if (!response && request) {
      errorHandler(new Error('Network Error - Unable to reach server'), {
        tags: {
          errorType: 'network_error',
          url: config?.url,
        },
        extra: {
          config,
          isNetworkError: true,
        },
      });
    }
    // HTTP error response
    else if (response) {
      const isClientError = response.status >= 400 && response.status < 500;
      const isServerError = response.status >= 500;

      if (isServerError || (isClientError && response.status !== 404)) {
        errorHandler(new Error(`HTTP ${response.status}: ${response.statusText}`), {
          tags: {
            errorType: isServerError ? 'server_error' : 'client_error',
            httpStatus: response.status.toString(),
            url: config?.url,
          },
          extra: {
            responseData: response.data,
            config,
            headers: response.headers,
          },
        });
      }
    }
    // Request setup error
    else {
      errorHandler(error, {
        tags: { errorType: 'request_setup_error' },
        extra: { config },
      });
    }

    return Promise.reject(error);
  }
);

// Setup global axios defaults
export const setupAxiosInterceptors = () => {
  // Apply request interceptor to the global axios instance
  axios.interceptors.request.use(
    (config) => {
      // Add request timing
      config.metadata = { startTime: new Date() };
      return config;
    },
    (error) => {
      errorHandler(error, {
        tags: { errorType: 'request_interceptor' },
        extra: { config: error.config },
      });
      return Promise.reject(error);
    }
  );

  // Apply response interceptor to the global axios instance
  axios.interceptors.response.use(
    (response) => {
      // Log slow requests
      if (response.config.metadata) {
        const endTime = new Date();
        const duration = endTime - response.config.metadata.startTime;

        if (duration > 5000) {
          // Log requests taking more than 5 seconds
          logWarning(`Slow API request detected`, {
            url: response.config.url,
            duration: `${duration}ms`,
            status: response.status,
          });
        }
      }

      return response;
    },
    (error) => {
      const { response, request, config } = error;

      // Network error
      if (!response && request) {
        errorHandler(new Error('Network Error - Unable to reach server'), {
          tags: {
            errorType: 'network_error',
            url: config?.url,
          },
          extra: {
            config,
            isNetworkError: true,
          },
        });
      }
      // HTTP error response
      else if (response) {
        const isClientError = response.status >= 400 && response.status < 500;
        const isServerError = response.status >= 500;

        if (isServerError || (isClientError && response.status !== 404)) {
          errorHandler(new Error(`HTTP ${response.status}: ${response.statusText}`), {
            tags: {
              errorType: isServerError ? 'server_error' : 'client_error',
              httpStatus: response.status.toString(),
              url: config?.url,
            },
            extra: {
              responseData: response.data,
              config,
              headers: response.headers,
            },
          });
        }
      }
      // Request setup error
      else {
        errorHandler(error, {
          tags: { errorType: 'request_setup_error' },
          extra: { config },
        });
      }

      return Promise.reject(error);
    }
  );
};

export default apiClient;
